import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "react-query";
import * as Sentry from "@sentry/react";
import App from "./App";
import reportWebVitals from "./reportWebVitals";

// Booking forms carry student PII (name, surname, phone, email, leadData).
// Scrub matching keys from any object nested in the event before send.
const PII_KEYS = [
  "name",
  "surname",
  "phone",
  "prePhoneCode",
  "email",
  "leadData",
];
const scrubPii = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(scrubPii);
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = PII_KEYS.includes(k) ? "[redacted]" : scrubPii(v);
    }
    return out;
  }
  return value;
};

// Stable anonymous session id, lets Sentry correlate multiple events from
// the same user without storing any PII. Survives across reloads in the same
// browser; resets when storage is cleared. Wrapped in try/catch because the
// booking form runs inside a third-party iframe (Kettydo) where storage may
// be blocked by browser privacy settings.
const getOrCreateAnonSessionId = (): string | undefined => {
  try {
    const key = "nabaSentrySessionId";
    let id = window.localStorage.getItem(key);
    if (!id) {
      id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem(key, id);
    }
    return id;
  } catch {
    return undefined;
  }
};

const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: process.env.REACT_APP_SENTRY_ENV ?? "unknown",
    release: process.env.REACT_APP_SENTRY_RELEASE,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
    ],
    beforeSend(event) {
      if (event.request?.data) {
        event.request.data = scrubPii(event.request.data) as typeof event.request.data;
      }
      if (event.extra) {
        event.extra = scrubPii(event.extra) as typeof event.extra;
      }
      // Dedupe identical fetch/xhr breadcrumbs — keep only the latest per URL.
      // Resource-list refetches (countries, regions, cities, …) fire 9× on
      // booking-form load; without this they eat the 100-breadcrumb cap and
      // crowd out the actual user actions we need for triage.
      if (event.breadcrumbs) {
        const seenUrls = new Set<string>();
        event.breadcrumbs = event.breadcrumbs
          .slice()
          .reverse()
          .filter((b) => {
            if (b.category !== "fetch" && b.category !== "xhr") return true;
            const url = (b.data?.url as string) ?? "";
            if (seenUrls.has(url)) return false;
            seenUrls.add(url);
            return true;
          })
          .reverse();
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) {
        breadcrumb.data = scrubPii(breadcrumb.data) as typeof breadcrumb.data;
      }
      return breadcrumb;
    },
  });
  const anonId = getOrCreateAnonSessionId();
  if (anonId) {
    Sentry.setUser({ id: anonId });
  }
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<div>Something went wrong.</div>}>
      <QueryClientProvider client={new QueryClient()}>
        <App />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
